import { QueryKey, MAGIC_LINK_CHALLENGE } from '@/enums';
import { useInitForgotPasswordMutation } from '@/hooks/useInitForgotPasswordMutation';
import { TenantEntity } from '@/interfaces';
import { PUBLIC_ROUTES } from '@/routes/routes';
import { errorNotification } from '@/utils';
import {
  ControlledInput,
  CustomButton,
  CustomModal,
} from '@amili-v3/amili-v3-design';
import { useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { get } from 'lodash';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { TextWrapper } from './styles';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const mailRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(true);

  const tenantData = queryClient.getQueryData([QueryKey.SUB_DOMAIN]);
  const tenant = get(tenantData, 'data', {}) as TenantEntity;
  const { id: tenantId } = tenant;

  const resetUserEmailMutation = useInitForgotPasswordMutation({
    onSuccess: ({ data }) => {
      const { challenge } = data;
      notification.success({ message: t('authentication.resetLinkSent') });
      setIsOpen(false);

      setTimeout(() => {
        if (challenge === MAGIC_LINK_CHALLENGE) {
          return navigate(PUBLIC_ROUTES.SIGN_IN);
        }

        navigate(
          generatePath(
            PUBLIC_ROUTES.RESET_PASSWORD + `?email=${mailRef.current}`,
          ),
        );
      }, 200);
    },
    onError: error => errorNotification({ error }),
  });

  const handleSubmit = (email: string) => {
    mailRef.current = email;
    return resetUserEmailMutation.mutate({
      email,
      tenantId,
    });
  };

  return (
    <CustomModal
      centered
      open={isOpen}
      mask={false}
      title={t('authentication.forgotPassword')}
      hideCloseButton
      layout="vertical"
      goBack={() => {
        setIsOpen(false);
        setTimeout(() => {
          navigate(PUBLIC_ROUTES.SIGN_IN);
        }, 200);
      }}
      formikProps={{
        validationSchema: Yup.object().shape({
          email: Yup.string().email().required('required'),
        }),
        initialValues: { email: '' },
        onSubmit: value => handleSubmit(value?.email),
      }}
      footer={[
        <CustomButton
          size="large"
          key="submit"
          form="modal-id"
          htmlType="submit"
          aria-label="Reset btn"
          loading={resetUserEmailMutation.isPending}
        >
          {t('common.reset')}
        </CustomButton>,
      ]}
    >
      <TextWrapper>{t('authentication.forgotPasswordHelp')}</TextWrapper>
      <ControlledInput
        label={t('common.email')}
        name="email"
        required
        aria-label="Email inp"
      />
    </CustomModal>
  );
};

export default ForgotPassword;
